import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { MdArrowBack, MdArrowOutward, MdArrowForward } from 'react-icons/md'
import { projects, getProject, hasCaseStudy } from './projectData'
import { NotFound } from '../NotFound'
import { RevealLine, RevealUp } from '../ui/Reveal'

const Label = ({ children }) => (
  <h2 className=' font-mono text-xs uppercase tracking-[0.2em] text-ink/40'>{children}</h2>
)

// Every block is optional — a study can be filled in a piece at a time and the
// page still reads as finished, because empty sections never mount.
const Block = ({ title, children }) => (
  <RevealUp className=' grid md:grid-cols-12 gap-6 md:gap-10 border-t border-ink/10 pt-8'>
    <div className=' md:col-span-3'>
      <Label>{title}</Label>
    </div>
    <div className=' md:col-span-8'>{children}</div>
  </RevealUp>
)

const Meta = ({ label, children }) => (
  <div>
    <p className=' font-mono text-[11px] uppercase tracking-[0.15em] text-ink/35'>{label}</p>
    <p className=' mt-1.5 text-sm text-ink/80'>{children}</p>
  </div>
)

export const CaseStudy = () => {
  const { slug } = useParams()
  const project = getProject(slug)

  if (!project || !hasCaseStudy(project)) return <NotFound />

  const study = project.caseStudy
  const index = projects.findIndex((p) => p.slug === project.slug)
  // Wrap around, skipping projects that have no study of their own to read.
  const next = projects
    .slice(index + 1)
    .concat(projects.slice(0, index))
    .find(hasCaseStudy)

  return (
    <article className=' px-6 md:px-10 py-16 md:py-24 border-b border-ink/10'>
      <div className=' max-w-7xl mx-auto'>
        <Link
          to='/projects'
          className=' group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-ink/50 hover:text-mint-deep transition-colors'>
          <MdArrowBack className=' text-base transition-transform duration-300 group-hover:-translate-x-0.5' />
          All work
        </Link>

        {/* title */}
        <header className=' mt-10 md:mt-14'>
          <p className=' font-mono text-xs uppercase tracking-[0.15em] text-mint-deep'>
            {project.stack.join(' / ')} — {project.year}
          </p>
          <h1 className=' font-display font-semibold text-maroon text-[clamp(2.5rem,8vw,6rem)] leading-[0.95] tracking-[-0.02em] mt-4'>
            <RevealLine>{project.title}</RevealLine>
          </h1>
          <p className=' mt-8 font-display text-xl md:text-3xl leading-[1.3] text-ink tracking-tight max-w-3xl'>
            {study.summary || project.blurb}
          </p>
        </header>

        {/* hero */}
        <RevealUp className=' mt-12 md:mt-16 overflow-hidden rounded-2xl border border-ink/10'>
          <div className=' aspect-[16/10] w-full'>
            <img src={project.path} alt={project.title} className=' h-full w-full object-cover' />
          </div>
        </RevealUp>

        {/* meta strip */}
        <div className=' mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-ink/10 pt-8'>
          {study.role && <Meta label='Role'>{study.role}</Meta>}
          {study.timeline && <Meta label='Timeline'>{study.timeline}</Meta>}
          <Meta label='Stack'>{project.stack.join(', ')}</Meta>
          <div>
            <p className=' font-mono text-[11px] uppercase tracking-[0.15em] text-ink/35'>Live</p>
            <a
              href={project.liveURL}
              target='_blank'
              rel='noopener noreferrer'
              className=' group mt-1.5 inline-flex items-center gap-1.5 text-sm text-maroon hover:text-mint-deep transition-colors break-all'>
              Visit site
              <MdArrowOutward className=' text-base shrink-0 transition-transform duration-300 group-hover:rotate-45' />
            </a>
          </div>
        </div>

        <div className=' mt-16 md:mt-24 flex flex-col gap-12 md:gap-16'>
          {study.problem && (
            <Block title='The problem'>
              <p className=' text-base md:text-lg leading-relaxed text-ink/70'>{study.problem}</p>
            </Block>
          )}

          {study.approach?.length > 0 && (
            <Block title='What I built'>
              <ul className=' flex flex-col'>
                {study.approach.map((item, i) => (
                  <li
                    key={i}
                    className=' flex gap-4 md:gap-6 border-b border-ink/10 py-4 first:pt-0 last:border-b-0'>
                    <span className=' font-mono text-xs text-ink/30 pt-1.5 shrink-0'>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className=' text-base md:text-lg leading-relaxed text-ink/70'>{item}</span>
                  </li>
                ))}
              </ul>
            </Block>
          )}

          {study.decisions?.length > 0 && (
            <Block title='Decisions'>
              <div className=' flex flex-col gap-8'>
                {study.decisions.map((d, i) => (
                  <div key={i} className=' border-l-2 border-mint-deep/40 pl-5 md:pl-6'>
                    <h3 className=' font-display text-xl md:text-2xl text-maroon tracking-tight'>
                      {d.title}
                    </h3>
                    <p className=' mt-2.5 text-base leading-relaxed text-ink/65'>{d.body}</p>
                    {d.link && (
                      <a
                        href={d.link.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className=' group mt-3 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-ink/50 hover:text-mint-deep transition-colors'>
                        {d.link.label}
                        <MdArrowOutward className=' text-base transition-transform duration-300 group-hover:rotate-45' />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </Block>
          )}

          {study.outcome && (
            <Block title='Outcome'>
              <p className=' text-base md:text-lg leading-relaxed text-ink/70'>{study.outcome}</p>
            </Block>
          )}
        </div>

        {/* next / visit */}
        <div className=' mt-20 md:mt-28 border-t border-ink/15 pt-10 flex flex-col md:flex-row md:items-end justify-between gap-8'>
          {next ? (
            <Link to={`/projects/${next.slug}`} className=' group max-w-xl'>
              <p className=' font-mono text-xs uppercase tracking-[0.18em] text-ink/40'>
                Next project
              </p>
              <h3 className=' mt-3 inline-flex items-center gap-3 font-display text-3xl md:text-5xl text-maroon tracking-tight transition-transform duration-300 group-hover:md:translate-x-2'>
                {next.title}
                <MdArrowForward className=' text-2xl text-ink/30 transition-colors duration-300 group-hover:text-mint-deep' />
              </h3>
            </Link>
          ) : (
            <div />
          )}
          <a
            href={project.liveURL}
            target='_blank'
            rel='noopener noreferrer'
            className=' group inline-flex items-center gap-2 shrink-0 bg-maroon text-cream text-sm font-medium px-7 py-3.5 rounded-full transition-colors duration-300 hover:bg-mint hover:text-panel w-fit'>
            Visit {project.title}
            <MdArrowOutward className=' text-base transition-transform duration-300 group-hover:rotate-45' />
          </a>
        </div>
      </div>
    </article>
  )
}
